import React from 'react';
import { Plus, Edit, Trash2, Search, Bed } from 'lucide-react';
import { useCrud, useModal, useForm } from '../hooks';
import { quartoService, localService } from '../services/api';

const Quartos = () => {
  const { data: quartos, loading, error, create, update, remove } = useCrud(quartoService, 'quartos');
  const { data: locais } = useCrud(localService, 'locais');
  const modal = useModal();
  const [searchTerm, setSearchTerm] = React.useState('');

  const validationRules = {
    numero: { required: true, label: 'Número' },
    local_id: { required: true, label: 'Local' },
    capacidade: { 
      required: true, 
      label: 'Capacidade',
      custom: (value) => {
        const num = parseInt(value);
        if (isNaN(num) || num < 1) {
          return 'Capacidade deve ser um número maior que 0';
        }
        return null;
      }
    },
    genero: { required: true, label: 'Gênero' }
  };

  const form = useForm(
    { numero: '', local_id: '', capacidade: '', tipo: '', genero: '' },
    validationRules
  );

  const filteredQuartos = quartos.filter(quarto =>
    quarto.numero?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    quarto.Local?.nome?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreate = () => {
    form.reset();
    modal.open({ mode: 'create', title: 'Novo Quarto' });
  };

  const handleEdit = (quarto) => {
    form.reset({
      numero: quarto.numero || '',
      local_id: quarto.local_id || '',
      capacidade: quarto.capacidade || '',
      tipo: quarto.tipo || '',
      genero: quarto.genero || ''
    });
    modal.open({ mode: 'edit', title: 'Editar Quarto', id: quarto.id });
  };

  const handleDelete = async (quarto) => {
    if (window.confirm(`Deseja realmente excluir o quarto "${quarto.numero}"?`)) {
      await remove(quarto.id);
    }
  };

  const handleSubmit = async (values) => {
    const result = modal.data.mode === 'create' 
      ? await create(values)
      : await update(modal.data.id, values);

    if (result.success) {
      modal.close();
      form.reset();
    }
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Quartos</h1>
        <button onClick={handleCreate} className="btn btn-primary">
          <Plus size={20} />
          Novo Quarto
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="card mb-6">
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar quartos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input pl-10"
            />
          </div>
        </div>
      </div>

      <div className="card">
        {filteredQuartos.length === 0 ? (
          <div className="empty-state">
            <Bed className="empty-state-icon" />
            <h3 className="text-lg font-medium mb-2">Nenhum quarto encontrado</h3>
            <p className="mb-4">
              {searchTerm ? 'Tente ajustar os filtros de busca.' : 'Comece criando um novo quarto.'}
            </p>
            {!searchTerm && (
              <button onClick={handleCreate} className="btn btn-primary">
                <Plus size={20} />
                Novo Quarto
              </button>
            )}
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Número</th>
                  <th>Local</th>
                  <th>Capacidade</th>
                  <th>Gênero</th>
                  <th>Tipo</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredQuartos.map((quarto) => (
                  <tr key={quarto.id}>
                    <td className="font-medium">{quarto.numero}</td>
                    <td>{quarto.Local?.nome || 'N/A'}</td>
                    <td>{quarto.capacidade}</td>
                    <td>{quarto.genero}</td>
                    <td>{quarto.tipo || '-'}</td>
                    <td>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(quarto)}
                          className="btn btn-sm btn-secondary"
                          title="Editar"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(quarto)}
                          className="btn btn-sm btn-danger"
                          title="Excluir"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {modal.isOpen && (
        <div className="modal-overlay" onClick={modal.close}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{modal.data.title}</h2>
              <button onClick={modal.close} className="modal-close">×</button>
            </div>

            <form onSubmit={form.handleSubmit(handleSubmit)}>
              <div className="form-group">
                <label className="form-label">Número *</label>
                <input
                  type="text"
                  value={form.values.numero}
                  onChange={(e) => form.setValue('numero', e.target.value)}
                  onBlur={() => form.setTouched('numero')}
                  className={`form-input ${form.errors.numero && form.touched.numero ? 'border-red-500' : ''}`}
                  placeholder="Digite o número do quarto"
                />
                {form.errors.numero && form.touched.numero && (
                  <div className="form-error">{form.errors.numero}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Local *</label>
                <select
                  value={form.values.local_id}
                  onChange={(e) => form.setValue('local_id', e.target.value)}
                  onBlur={() => form.setTouched('local_id')}
                  className={`form-select ${form.errors.local_id && form.touched.local_id ? 'border-red-500' : ''}`}
                >
                  <option value="">Selecione um local</option>
                  {locais.map((local) => (
                    <option key={local.id} value={local.id}>
                      {local.nome}
                    </option>
                  ))}
                </select>
                {form.errors.local_id && form.touched.local_id && (
                  <div className="form-error">{form.errors.local_id}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Capacidade *</label>
                <input
                  type="number"
                  value={form.values.capacidade}
                  onChange={(e) => form.setValue('capacidade', e.target.value)}
                  onBlur={() => form.setTouched('capacidade')}
                  className={`form-input ${form.errors.capacidade && form.touched.capacidade ? 'border-red-500' : ''}`}
                  placeholder="Digite a capacidade do quarto"
                  min="1"
                />
                {form.errors.capacidade && form.touched.capacidade && (
                  <div className="form-error">{form.errors.capacidade}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Gênero *</label>
                <select
                  value={form.values.genero}
                  onChange={(e) => form.setValue('genero', e.target.value)}
                  onBlur={() => form.setTouched('genero')}
                  className={`form-select ${form.errors.genero && form.touched.genero ? 'border-red-500' : ''}`}
                >
                  <option value="">Selecione o gênero</option>
                  <option value="masculino">Masculino</option>
                  <option value="feminino">Feminino</option>
                  <option value="misto">Misto</option>
                </select>
                {form.errors.genero && form.touched.genero && (
                  <div className="form-error">{form.errors.genero}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Tipo</label>
                <input
                  type="text"
                  value={form.values.tipo}
                  onChange={(e) => form.setValue('tipo', e.target.value)}
                  className="form-input"
                  placeholder="Digite o tipo do quarto"
                />
              </div>

              <div className="modal-footer">
                <button type="button" onClick={modal.close} className="btn btn-secondary">
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  {modal.data.mode === 'create' ? 'Criar' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Quartos;